"use client";

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Maximize2 } from "lucide-react";
import { ResponsiveModal } from "@west-tennessee-consulting/ui";
import { cn } from "@/lib/utils";
import type { BaseEntity } from "../UniversalCRMTable/types";
import type { InlineEditableInfoPanelProps } from "./types";
import { InlineEditableInfoPanelContent } from "./components/InlineEditableInfoPanelContent";

// Re-export types for backward compatibility
export type { InlineEditableInfoPanelProps };

// ============================================================================
// INLINE EDITABLE INFO PANEL (with optional expand to fullscreen)
// ============================================================================

export function InlineEditableInfoPanel<T extends BaseEntity>({
  item,
  config,
  onInlineEdit,
  title,
  showExpandButton = true,
  variant = "panel",
  className = "",
}: InlineEditableInfoPanelProps<T>) {
  const [expandOpen, setExpandOpen] = useState(false);
  const displayTitle = title ?? `${config.displayName} Information`;

  const content = (
    <InlineEditableInfoPanelContent
      item={item}
      config={config}
      onInlineEdit={onInlineEdit}
      variant={variant}
      displayTitle={displayTitle}
    />
  );

  return (
    <div className={cn("flex flex-col min-h-0", className)}>
      <div className="flex items-center justify-between gap-2 mb-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{displayTitle}</h3>
        {showExpandButton && variant === "panel" && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setExpandOpen(true)}
            className="h-8 px-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800"
            title="Expand to full screen"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">{content}</div>

      {showExpandButton && variant === "panel" && (
        <ResponsiveModal
          open={expandOpen}
          onOpenChange={setExpandOpen}
          title={displayTitle}
          maxWidth="90vw"
          side="bottom"
          className="min-h-[85vh]"
        >
          <InlineEditableInfoPanelContent
            item={item}
            config={config}
            onInlineEdit={onInlineEdit}
            variant="fullscreen"
            displayTitle={displayTitle}
          />
        </ResponsiveModal>
      )}
    </div>
  );
}
